import React, { useCallback, useEffect, useState } from 'react';
import { authHeaderOrEmpty } from '../../utils/authHeaders';
import { useModuleAccess } from '../../lib/featureFlags';
import { formatDateFR } from '../../utils/formatFR';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HEURE_PARIS = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
});
const dateParis = (iso) => {
  if (!iso) return '—';
  try { return HEURE_PARIS.format(new Date(iso)); } catch { return '—'; }
};

const LIBELLE_TYPE = { properties: 'Biens', contacts: 'Contacts' };

/**
 * Admin agence — carte « Import / Export » (lot CB).
 * Exports CSV de SA SEULE agence, historique des exports, demande de restauration.
 */
export const AgencyImportExportCard = () => {
  const { enabled } = useModuleAccess('import-export');
  const [historique, setHistorique] = useState([]);
  const [busy, setBusy] = useState('');
  const [erreur, setErreur] = useState('');
  const [demande, setDemande] = useState(null); // { date_visee, motif, envoi, envoye }

  const chargerHistorique = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/agence/export/history`, { headers: authHeaderOrEmpty() });
      if (!r.ok) return;
      const d = await r.json();
      setHistorique(d.items || []);
    } catch {
      /* l'historique est un confort : son échec ne bloque pas les exports */
    }
  }, []);

  useEffect(() => { chargerHistorique(); }, [chargerHistorique]);

  if (!enabled) return null;

  const exporter = async (dataType) => {
    setBusy(dataType);
    setErreur('');
    try {
      const r = await fetch(`${API_URL}/api/agence/export/${dataType}`, { headers: authHeaderOrEmpty() });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail?.message || d.detail || `Erreur ${r.status}`);
      }
      const blob = await r.blob();
      const nom = (r.headers.get('Content-Disposition') || '').split('filename=')[1] || `export_${dataType}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nom.replace(/"/g, '').trim();
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      chargerHistorique();
    } catch (e) {
      setErreur(e.message);
    } finally {
      setBusy('');
    }
  };

  const envoyerDemande = async () => {
    setDemande((d) => ({ ...d, envoi: true }));
    try {
      const fd = new FormData();
      fd.append('categorie', 'restauration');
      fd.append('urgence', 'eleve');
      fd.append('description',
        `Demande de restauration de données.\nDate visée : ${formatDateFR(demande.date_visee, { fallback: '(non précisée)' })}\nMotif : ${demande.motif}`);
      const r = await fetch(`${API_URL}/api/support/tickets`, {
        method: 'POST', headers: authHeaderOrEmpty(), body: fd,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Erreur ${r.status}`);
      setDemande((prev) => ({ ...prev, envoi: false, envoye: true }));
    } catch (e) {
      setDemande((prev) => ({ ...prev, envoi: false, erreur: e.message }));
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6" data-testid="agence-import-export-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
          <i className="bi bi-arrow-left-right text-[#007BFF] text-lg"></i>
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">Import / Export</h2>
          <p className="text-sm text-slate-500">Exportez vos biens et vos contacts au format CSV</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:flex-wrap gap-2">
        <button
          onClick={() => exporter('properties')}
          disabled={busy === 'properties'}
          className="w-full md:w-auto px-4 h-11 md:h-auto md:py-2 text-sm font-semibold rounded-lg bg-[#0062CC] hover:bg-[#0056b3] text-white shadow-sm disabled:opacity-60"
          data-testid="agence-export-properties"
        >
          <i className={`bi ${busy === 'properties' ? 'bi-arrow-clockwise animate-spin' : 'bi-download'} mr-1`}></i>
          Exporter les biens (CSV)
        </button>
        <button
          onClick={() => exporter('contacts')}
          disabled={busy === 'contacts'}
          className="w-full md:w-auto px-4 h-11 md:h-auto md:py-2 text-sm font-semibold rounded-lg bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
          data-testid="agence-export-contacts"
        >
          <i className={`bi ${busy === 'contacts' ? 'bi-arrow-clockwise animate-spin' : 'bi-download'} mr-1`}></i>
          Exporter les contacts (CSV)
        </button>
      </div>

      <p className="text-xs text-slate-500 mt-3">
        Les fichiers contiennent des données personnelles : conservez-les en lieu sûr.
      </p>

      {erreur && (
        <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-700" data-testid="agence-export-erreur">
          {erreur}
        </div>
      )}

      {/* Historique */}
      <div className="mt-5 border-t border-slate-100 pt-4">
        <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Derniers exports</h3>
        {historique.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="agence-export-history-vide">Aucun export pour le moment.</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm" data-testid="agence-export-history">
            {historique.map((h, i) => (
              <li key={i} className="py-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-slate-700">
                  {dateParis(h.timestamp)} — {LIBELLE_TYPE[h.data_type] || h.data_type}
                  <span className="text-slate-400"> · {h.rows} ligne(s)</span>
                </span>
                <span className="text-xs text-slate-500">{h.user_email}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Restauration */}
      <div className="mt-5 bg-slate-50 border border-slate-200 rounded-xl p-4">
        <p className="text-sm text-slate-700">
          <i className="bi bi-shield-check text-emerald-600 mr-1"></i>
          Vos données sont sauvegardées chaque nuit par Komerces.
        </p>
        <button
          onClick={() => setDemande({ date_visee: '', motif: '', envoi: false, envoye: false })}
          className="mt-2 w-full md:w-auto px-3 h-11 md:h-auto md:py-2 text-xs font-semibold rounded-lg bg-white border border-slate-300 hover:bg-slate-50"
          data-testid="agence-demande-restauration"
        >
          <i className="bi bi-life-preserver mr-1"></i>Demander une restauration
        </button>
        <p className="text-xs text-slate-500 mt-2">
          Traitée par Komerces. Facturable si elle fait suite à une erreur de manipulation.
        </p>
      </div>

      {demande && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" data-testid="agence-restauration-modal">
            <h3 className="font-bold text-lg text-slate-900 mb-1">Demander une restauration</h3>
            <p className="text-xs text-slate-500 mb-4">Votre demande arrive directement au support Komerces.</p>

            {demande.envoye ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800" data-testid="agence-restauration-ok">
                Demande envoyée. Le support Komerces vous répond depuis la messagerie de vos signalements.
              </div>
            ) : (
              <>
                <label className="block text-xs font-bold text-slate-600 mb-1.5">Date visée</label>
                <input
                  type="date"
                  value={demande.date_visee}
                  onChange={(e) => setDemande({ ...demande, date_visee: e.target.value })}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 mb-3"
                  data-testid="agence-restauration-date"
                />
                <label className="block text-xs font-bold text-slate-600 mb-1.5">Motif</label>
                <textarea
                  rows={4}
                  value={demande.motif}
                  onChange={(e) => setDemande({ ...demande, motif: e.target.value })}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2"
                  placeholder="Ce qui a été perdu ou modifié, et quand."
                  data-testid="agence-restauration-motif"
                />
                {demande.erreur && (
                  <p className="text-sm text-rose-700 mt-2" data-testid="agence-restauration-erreur">{demande.erreur}</p>
                )}
              </>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setDemande(null)}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-300 hover:bg-slate-50"
                data-testid="agence-restauration-fermer"
              >
                {demande.envoye ? 'Fermer' : 'Annuler'}
              </button>
              {!demande.envoye && (
                <button
                  onClick={envoyerDemande}
                  disabled={demande.envoi || !demande.motif.trim()}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-[#0062CC] hover:bg-[#0056b3] text-white shadow-sm disabled:opacity-50"
                  data-testid="agence-restauration-envoyer"
                >
                  {demande.envoi ? 'Envoi…' : 'Envoyer la demande'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyImportExportCard;
